import { format as formatLocalDate } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";

const LAB_TIME_ZONES = {
  EST: "America/New_York",
  CST: "America/Chicago",
  MST: "America/Denver",
  PST: "America/Los_Angeles",
  HST: "Pacific/Honolulu",
} as const;

type LabTimeZoneKey = keyof typeof LAB_TIME_ZONES;
type RequisitionRow = Record<string, unknown>;

const TIME_ZONE_COLUMNS = new Set([
  "dateofcollection",
  "timeofcollection",
  "receiveddate",
]);

const getRowValue = (
  rowData: RequisitionRow | null | undefined,
  columnKey: string
) => {
  if (!rowData || !columnKey) return undefined;

  const matchingKey = Object.keys(rowData).find(
    (key) => key.toLowerCase() === columnKey.toLowerCase()
  );

  return matchingKey ? rowData[matchingKey] : undefined;
};

const getLabTimeZone = (value?: string | null) => {
  const key = value?.trim().toUpperCase() as LabTimeZoneKey | undefined;

  if (!key || !LAB_TIME_ZONES[key]) return null;

  return {
    key,
    timeZone: LAB_TIME_ZONES[key],
  };
};

const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const getLabDateTimeParts = (
  dateValue: unknown,
  timeValue?: unknown
): string | null => {
  if (typeof dateValue !== "string" || !dateValue.trim()) return null;

  const value = dateValue.trim();
  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](.+))?/);
  const usDateMatch = value.match(
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[ T](.+))?/
  );

  const year = isoDateMatch?.[1] ?? usDateMatch?.[3];
  const month = isoDateMatch?.[2] ?? usDateMatch?.[1];
  const day = isoDateMatch?.[3] ?? usDateMatch?.[2];
  const embeddedTime = isoDateMatch?.[4] ?? usDateMatch?.[4];

  if (!year || !month || !day) return null;

  const calendarDate = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day))
  );
  if (
    calendarDate.getUTCFullYear() !== Number(year) ||
    calendarDate.getUTCMonth() !== Number(month) - 1 ||
    calendarDate.getUTCDate() !== Number(day)
  ) {
    return null;
  }

  const rawTime =
    typeof timeValue === "string" && timeValue.trim()
      ? timeValue.trim()
      : (embeddedTime ?? "00:00:00");
  const timeMatch = rawTime.match(
    /^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?\s*(AM|PM)?/i
  );

  if (!timeMatch) return null;

  let hour = Number(timeMatch[1]);
  const period = timeMatch[4]?.toUpperCase();

  if (period) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (period === "PM" ? 12 : 0);
  } else if (hour > 23) {
    return null;
  }

  return `${year}-${month.padStart(2, "0")}-${day.padStart(
    2,
    "0"
  )}T${String(hour).padStart(2, "0")}:${timeMatch[2]}:${timeMatch[3] ?? "00"}`;
};

/** Converts a date/time entered in the lab's timezone into a UTC instant. */
export const convertLabDateTimeToUtc = (
  dateValue: unknown,
  timeValue: unknown,
  labTimeZone?: string | null
): Date | null => {
  const zone = getLabTimeZone(labTimeZone);
  const labDateTime = getLabDateTimeParts(dateValue, timeValue);

  if (!zone || !labDateTime) return null;

  const utcDate = zonedTimeToUtc(labDateTime, zone.timeZone);
  return Number.isNaN(utcDate.getTime()) ? null : utcDate;
};

/** Converts a date/time entered in the browser's timezone into a UTC instant. */
export const convertBrowserDateTimeToUtc = (
  dateValue: unknown,
  timeValue?: unknown
): Date | null => {
  const browserDateTime = getLabDateTimeParts(dateValue, timeValue);
  if (!browserDateTime) return null;

  const browserTimeZone = getBrowserTimeZone();
  const utcDate = browserTimeZone
    ? zonedTimeToUtc(browserDateTime, browserTimeZone)
    : new Date(browserDateTime);

  return Number.isNaN(utcDate.getTime()) ? null : utcDate;
};

export const formatUtcTimeForApi = (utcDate: Date) =>
  formatInTimeZone(utcDate, "UTC", "HH:mm:ss");

/**
 * The API stores UTC in SQL datetime columns, which serializes without a zone.
 * Add the UTC designator explicitly so the browser never treats it as local time.
 */
const parseUtcDateTime = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== "string" || !value.trim()) return null;

  const normalizedValue = value.trim().replace(" ", "T");
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue);
  const parsedDate = new Date(
    hasZone ? normalizedValue : `${normalizedValue}Z`
  );
  if (!Number.isNaN(parsedDate.getTime())) return parsedDate;

  // Fallback ONLY for inputs the ISO parse above could not handle (e.g. the
  // "MM/DD/YYYY HH:mm:ss" format the edit API returns for DateReceived).
  // Reuse the existing normalizer, then parse as UTC. If it still cannot be
  // parsed, return null exactly as before, so no working path changes.
  const isoDateTime = getLabDateTimeParts(value);
  if (!isoDateTime) return null;
  const fallbackDate = new Date(`${isoDateTime}Z`);
  return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
};

const parseUtcDateAndTime = (
  dateValue: unknown,
  timeValue: unknown
): Date | null => {
  const utcDateTime = getLabDateTimeParts(dateValue, timeValue);
  return utcDateTime ? parseUtcDateTime(utcDateTime) : null;
};

const formatInBrowserTimeZone = (date: Date, format: string) => {
  const browserTimeZone = getBrowserTimeZone();
  return browserTimeZone
    ? formatInTimeZone(date, browserTimeZone, format)
    : formatLocalDate(date, format);
};

/**
 * Converts separate UTC date/time values to browser-local form values.
 * These values are intended for editable controls and deliberately omit a
 * timezone label.
 */
export const formatUtcDateTimePairInBrowserTimeZone = (
  dateValue: unknown,
  timeValue: unknown
): { date: string; time: string } | null => {
  const utcDate = parseUtcDateAndTime(dateValue, timeValue);
  if (!utcDate) return null;

  return {
    date: formatInBrowserTimeZone(utcDate, "MM/dd/yyyy"),
    time: formatInBrowserTimeZone(utcDate, "HH:mm:ss"),
  };
};

/** Converts one UTC datetime value to a browser-local editable value. */
export const formatUtcDateTimeInBrowserTimeZone = (
  value: unknown,
  format = "yyyy-MM-dd'T'HH:mm:ss"
): string | null => {
  const utcDate = parseUtcDateTime(value);
  return utcDate ? formatInBrowserTimeZone(utcDate, format) : null;
};

/**
 * Combines separate UTC date/time fields and displays the resulting instant in
 * the selected lab's timezone. Returning null lets the caller keep the API values.
 */
export const formatUtcDateTimePairInLabTimeZone = (
  dateValue: unknown,
  timeValue: unknown,
  labTimeZone?: string | null,
  dateFormat = "MM/dd/yyyy",
  timeFormat = "HH:mm:ss"
): { date: string; time: string } | null => {
  const zone = getLabTimeZone(labTimeZone);
  const utcDate = parseUtcDateAndTime(dateValue, timeValue);

  if (!zone || !utcDate) return null;

  return {
    date: `${formatInTimeZone(utcDate, zone.timeZone, dateFormat)} (${zone.key})`,
    time: `${formatInTimeZone(utcDate, zone.timeZone, timeFormat)} (${zone.key})`,
  };
};

const getCollectionUtcDateTime = (
  rowData: RequisitionRow | null | undefined
): Date | null => {
  const dateValue = getRowValue(rowData, "DateofCollection");
  const timeValue = getRowValue(rowData, "TimeofCollection");

  if (typeof dateValue === "string" && typeof timeValue === "string") {
    const datePart = dateValue.trim().match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
    const timePart = timeValue
      .trim()
      .match(/^([01]?\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,7})?)?/)?.[0];

    if (datePart && timePart) {
      return parseUtcDateTime(`${datePart}T${timePart}`);
    }
  }

  return parseUtcDateTime(dateValue);
};

/**
 * Formats only the three View Requisition UTC columns in the selected lab's
 * timezone. `undefined` tells the caller to retain its existing formatter.
 */
export const formatViewRequisitionLabTime = (
  rowData: RequisitionRow,
  columnKey: string,
  labTimeZone?: string | null
): string | undefined => {
  const normalizedColumnKey = columnKey?.toLowerCase();

  if (!TIME_ZONE_COLUMNS.has(normalizedColumnKey)) return undefined;

  const zone = getLabTimeZone(labTimeZone);
  if (!zone) return undefined;

  const columnValue = getRowValue(rowData, columnKey);
  if (!columnValue) return "";

  const utcDate =
    normalizedColumnKey === "dateofcollection" ||
    normalizedColumnKey === "timeofcollection"
      ? getCollectionUtcDateTime(rowData)
      : parseUtcDateTime(columnValue);

  if (!utcDate) return undefined;

  const format =
    normalizedColumnKey === "timeofcollection" ? "hh:mm:ss a" : "MM/dd/yyyy";

  return `${formatInTimeZone(utcDate, zone.timeZone, format)} (${zone.key})`;
};
